import Modal from '../Modal/Modal';
import './SignInModal.scss';
import { useEffect, useState } from 'react';
import SubmitOtpForm from './OtpForm/SubmitOtpForm';
import { LoadingIndicator } from '../cart';
import RequestOTPForm from './OtpForm/RequestOTPForm';
import { observer } from 'mobx-react-lite';
import { translate } from '@micro-apps/env';
import { useRecaptcha } from '@fshr/hooks';
import { analyticEvents } from '@micro-apps/shared';
import type { UserStore } from '@micro-apps/stores';
import { GlobalStore } from '@fshr/stores';
import { FSLocale } from '@fshr/shared';

const SignInModal = ({
	closeModal,
	onSignInSuccess,
	isPersistentlySignedIn,
	userStore,
	globalStore,
	locale,
}: {
	closeModal: () => unknown;
	onSignInSuccess?: () => void;
	isPersistentlySignedIn: boolean;
	userStore: UserStore;
	globalStore: GlobalStore;
	locale: FSLocale;
}) => {
	const [sendCodeTo, setSendCodeTo] = useState('');
	const [loginType, setLoginType] = useState('EMAIL_ADDRESS');
	const [isloading, setIsLoading] = useState(false);
	const [isError, setIsError] = useState(false);
	const [errorMessage, setErrorMessage] = useState();

	const {
		recaptchaElementId,
		recaptchaCallbackName,
		// TODO: use isReady in reCapture
		// isReady,
		recaptchaExecute,
		recaptchaReset,
	} = useRecaptcha({ fsLocale: locale });

	const { requestOtp, submitOtp, isOtpRequested, setOtpRequested, profile } = userStore;

	useEffect(() => {
		analyticEvents.signInFormShown();
	}, []);

	const resetFlow = () => {
		setIsError(false);
		recaptchaReset();
		setOtpRequested(false);
		setLoginType('EMAIL_ADDRESS');
	};

	const handleRequest = async () => {
		try {
			setIsLoading(true);
			const gRecaptchaResponse = await recaptchaExecute();
			await requestOtp(sendCodeTo, gRecaptchaResponse, loginType);
			setIsLoading(false);
		} catch (error: any) {
			setErrorMessage(
				error?.response?.data?.supplierErrors?.[0]?.errorCode ||
					error?.response?.data?.apiErrors?.[0]?.userMessage
			);
			recaptchaReset();
			setIsError(true);
			setIsLoading(false);
		}
	};

	const handleSubmit = async (sendCodeTo, OTP, sendCodeVia, persistLogin) => {
		try {
			setIsLoading(true);
			await submitOtp(sendCodeTo, OTP, sendCodeVia, persistLogin);
			userStore.fetchProfile();
			globalStore.refreshState();
			window.FS?.['tooltipsController']?.add({
				text: translate('You have signed in successfully.'),
				id: `success-sign-in`,
				isSession: true,
				dismissTimeout: 4000,
				dismissIcon: true,
				isSignedInModal: true,
			});
			// Trigger interaction tracking after successful OTP submission
			const loginMethod = sendCodeVia === 'EMAIL_ADDRESS' ? 'email' : 'phone';
			const enablePersistentLogin = persistLogin ? 'enabled' : 'disabled';
			const loginType = isPersistentlySignedIn ? 're-authentication' : 'full login';

			analyticEvents.signInFormSubmitted({
				loginMethod,
				loginType,
				enablePersistentLogin,
			});
			closeModal();
			setIsLoading(false);
			onSignInSuccess?.();
		} catch {
			setIsError(true);
			setIsLoading(false);
		}
	};

	return (
		<Modal close={closeModal}>
			<div className="form-wrapper">
				{isloading && (
					<div className="loading-container">
						<LoadingIndicator />
					</div>
				)}
				<div className="otp-sign-in">
					{isOtpRequested ? (
						<SubmitOtpForm
							sendCodeVia={loginType}
							sendCodeTo={sendCodeTo}
							resetFlow={resetFlow}
							isError={isError}
							handleSubmit={handleSubmit}
							locale={locale}
						/>
					) : (
						<RequestOTPForm
							locale={locale}
							sendCodeTo={sendCodeTo}
							loginType={loginType}
							isError={isError}
							errorMessage={errorMessage}
							handleRequest={handleRequest}
							setSendCodeTo={setSendCodeTo}
							setLoginType={setLoginType}
							setIsError={setIsError}
							isPersistentlySignedIn={isPersistentlySignedIn}
							profile={profile}
						/>
					)}
				</div>
			</div>
			<div
				id={recaptchaElementId}
				data-callback={recaptchaCallbackName}
				className="recaptcha-container"
			/>
		</Modal>
	);
};

export default observer(SignInModal);
